/** Goals + priorities CRUD, with the founder-editable pacing model. */
import { useState } from "react";
import { Empty, ErrorNote, Panel, Readout, useApi } from "../components/ui.js";
import { api } from "../lib/api.js";
import { fmtDate, int, n, usd } from "../lib/format.js";
import { DEFAULT_PACING_MODEL, steadyState, type PacingModel } from "../lib/pacing.js";
import type { GoalRow, PriorityRow } from "../lib/types.js";

const MODEL_FIELDS: Array<{ key: keyof PacingModel; label: string }> = [
  { key: "scope_arpu_usd", label: "Scope ARPU $/mo" },
  { key: "accelerator_arpu_usd", label: "Accelerator ARPU $/mo" },
  { key: "accel_upsell_rate", label: "Upsell rate (0–1)" },
  { key: "accel_lag_months_min", label: "Lag months · min" },
  { key: "accel_lag_months_max", label: "Lag months · max" },
];

export function Goals() {
  const goals = useApi<{ goals: GoalRow[] }>("/api/goals");
  const priorities = useApi<{ priorities: PriorityRow[] }>("/api/priorities");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [newPriority, setNewPriority] = useState("");

  const goal = goals.data?.goals.find((g) => g.metric === "arr_usd") ?? goals.data?.goals[0] ?? null;
  const model: PacingModel = { ...DEFAULT_PACING_MODEL, ...(goal?.pacing_model ?? {}) };
  const liveModel: PacingModel = { ...model };
  for (const f of MODEL_FIELDS) {
    if (draft[f.key] !== undefined && draft[f.key] !== "" && Number.isFinite(Number(draft[f.key]))) {
      liveModel[f.key] = Number(draft[f.key]);
    }
  }
  const steady = steadyState(liveModel, n(goal?.target_value) || 1_000_000);

  async function saveModel() {
    if (!goal) return;
    setBusy(true);
    try {
      await api.patch(`/api/goals/${goal.id}`, { pacingModel: liveModel });
      setDraft({});
      goals.reload();
    } finally { setBusy(false); }
  }

  async function addPriority() {
    if (!newPriority.trim()) return;
    setBusy(true);
    try {
      const rank = (priorities.data?.priorities.length ?? 0) + 1;
      await api.post("/api/priorities", { title: newPriority.trim(), rank });
      setNewPriority("");
      priorities.reload();
    } finally { setBusy(false); }
  }

  async function archivePriority(id: string) {
    setBusy(true);
    try { await api.del(`/api/priorities/${id}`); priorities.reload(); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="page-head"><h1>Goals</h1><span className="stamp">targets + the pacing model (data, not code)</span></div>

      <div className="grid cols-2" style={{ marginBottom: 14 }}>
        <Panel title="Governing goal">
          {goals.error ? <ErrorNote error={goals.error} /> : !goal ? <Empty>no goal — the seed creates arr-1m-dec-2026</Empty> : (
            <>
              <Readout hero label={goal.key} value={usd(goal.target_value, { compact: true })}
                       note={`${goal.metric} by ${fmtDate(goal.target_date)} · status ${goal.status}`} />
              <p className="dim" style={{ fontSize: 14 }}>{goal.description}</p>
            </>
          )}
        </Panel>

        <Panel title="Pacing model — edits apply instantly to the Brief">
          {!goal ? <Empty>—</Empty> : (
            <>
              <div className="grid cols-3">
                {MODEL_FIELDS.map((f) => (
                  <label key={f.key} className="fld">
                    <span>{f.label}</span>
                    <input
                      value={draft[f.key] ?? String(model[f.key])}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    />
                  </label>
                ))}
              </div>
              <hr className="rule" />
              <div className="row">
                <Readout label="Blended $/account/mo" value={usd(steady.blendedMrrPerAccount)} />
                <Readout label="Accounts at steady state" value={int(steady.accountsNeeded)} />
                <span className="spacer" />
                <button className="btn primary" disabled={busy || Object.keys(draft).length === 0} onClick={saveModel}>Save model</button>
              </div>
            </>
          )}
        </Panel>
      </div>

      <Panel title="Priorities — top of the Brief">
        <div className="row" style={{ marginBottom: 12 }}>
          <input style={{ flex: 1 }} placeholder="add a priority…" value={newPriority}
                 onChange={(e) => setNewPriority(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") void addPriority(); }} />
          <button className="btn sm" disabled={busy} onClick={addPriority}>Add</button>
        </div>
        {(priorities.data?.priorities ?? []).length === 0 ? <Empty>none yet</Empty> : (
          <table className="data">
            <tbody>
              {(priorities.data?.priorities ?? []).map((p, i) => (
                <tr key={p.id}>
                  <td className="num faint" style={{ width: 30 }}>{String(i + 1).padStart(2, "0")}</td>
                  <td>{p.title}</td>
                  <td style={{ width: 80, textAlign: "right" }}>
                    <button className="btn sm danger" disabled={busy} onClick={() => archivePriority(p.id)}>archive</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
